import { useCallback, useState } from 'react';
import * as api from '@encarpkg/api';
import { dayjs } from '@encarpkg/shared';
import { loggingException } from 'app/libs/sentry';

// 점검 창 시각은 Consul 설정으로 바뀔 수 있어 FE에 고정하지 않고 응답의 nextStartDateTime/nextEndDateTime 을 그대로 노출 (BE6-1454)
const formatMaintenanceTime = ({ nextStartDateTime, nextEndDateTime } = {}) => {
	if (!nextStartDateTime || !nextEndDateTime) return '';
	const start = dayjs(nextStartDateTime);
	const end = dayjs(nextEndDateTime);
	if (!start.isValid() || !end.isValid()) return '';
	return `${start.format('HH:mm')}~${end.format('HH:mm')}`;
};

// 정산 마감 점검 여부 확인 후 점검 안내 노출 상태 관리 (비차단)
export default function useMaintenanceCheck() {
	const [showMaintenance, setShowMaintenance] = useState(false);
	// 안내 문구용 점검 시간 "HH:mm~HH:mm" — 조회 전/실패 시 빈 문자열
	const [maintenanceTime, setMaintenanceTime] = useState('');

	// 점검 중이면 안내 노출. 점검 체크 자체가 실패해도 페이지 로딩은 막지 않음
	// 점검 여부 판단은 inMaintenance 로만 한다 — 두 일시는 점검 기능 off/강제 점검 상태에서도 설정값 기준으로 내려온다
	const checkMaintenance = useCallback(async () => {
		try {
			const { data } = await api.getMaintenanceCheck();
			setMaintenanceTime(formatMaintenanceTime(data));
			if (data?.inMaintenance) {
				setShowMaintenance(true);
				return true;
			}
		} catch (error) {
			loggingException(error);
		}
		return false;
	}, []);

	// 503 응답 바디에는 점검 시간이 없어 조회 API 로 시간을 보강 — 실패해도 안내는 이미 노출된 상태
	const fetchMaintenanceTime = useCallback(async () => {
		try {
			const { data } = await api.getMaintenanceCheck();
			setMaintenanceTime(formatMaintenanceTime(data));
		} catch (error) {
			loggingException(error);
		}
	}, []);

	// API 응답이 503이면 점검 안내 노출 (변경 동작 catch에서 사용). 처리 시 true 반환
	const handleServiceUnavailable = useCallback(
		(error) => {
			if (error?.response?.status === 503) {
				setShowMaintenance(true);
				fetchMaintenanceTime();
				return true;
			}
			return false;
		},
		[fetchMaintenanceTime],
	);

	return {
		showMaintenance,
		setShowMaintenance,
		maintenanceTime,
		checkMaintenance,
		handleServiceUnavailable,
	};
}
